import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { fontWeights } from "./element-schema";

/**
 * Zod schemas for brand-kit override types.
 *
 * These match the TS types in brand-kit.ts:
 *   Override<ElementStyleDefinition>
 *   Override<ButtonStyleDefinition>
 *   Override<InputStyleDefinition>
 *   Enableable<Override<...>>
 *
 * The "override" shape replaces `fontReference` (a key into the brand-kit font
 * hierarchy) with an inline `font` object containing resolved values.
 */

const elementStyleOverrideSchema_ = z.object({
  backgroundColor: z.string().nullish().meta({ privacy: privacy.nonPii }),
  textColor: z.string().meta({ privacy: privacy.nonPii }),
  border: z
    .object({
      cornerRadiusPx: z.number().meta({ privacy: privacy.nonPii }),
      stroke: z
        .object({
          color: z.string().meta({ privacy: privacy.nonPii }),
          weightPx: z.number().meta({ privacy: privacy.nonPii }),
        })
        .meta({ privacy: privacy.structured }),
    })
    .meta({ privacy: privacy.structured }),
  paddingPx: z.number().meta({ privacy: privacy.nonPii }),
  font: z
    .object({
      fontFamily: z.string().meta({ privacy: privacy.nonPii }),
      fontSizePx: z.number().meta({ privacy: privacy.nonPii }),
      fontWeight: createEnum(fontWeights, { enumName: "FontWeight" }).meta({
        privacy: privacy.nonPii,
      }),
    })
    .meta({ privacy: privacy.structured }),
});
export const elementStyleOverrideSchema: z.ZodType<ElementStyleOverride> =
  elementStyleOverrideSchema_;
export interface ElementStyleOverride extends z.infer<
  typeof elementStyleOverrideSchema_
> {}

export const buttonStyleOverrideSchema: z.ZodType<ButtonStyleOverride> =
  elementStyleOverrideSchema_;
export interface ButtonStyleOverride extends ElementStyleOverride {}

const inputStyleOverrideSchema_ = elementStyleOverrideSchema_.extend({
  errorColor: z.string().meta({ privacy: privacy.nonPii }),
});
export const inputStyleOverrideSchema: z.ZodType<InputStyleOverride> =
  inputStyleOverrideSchema_;
export interface InputStyleOverride extends z.infer<
  typeof inputStyleOverrideSchema_
> {}

/**
 * "Brand kit reference or override" — when enabled is false the consumer should
 * use the team's brand-kit values; when true, use the override values stored
 * here.
 */
const enableableButtonOverrideSchema_ = elementStyleOverrideSchema_.extend({
  enabled: z.boolean().meta({ privacy: privacy.nonPii }),
});
export const enableableButtonOverrideSchema: z.ZodType<EnableableButtonOverride> =
  enableableButtonOverrideSchema_;
export interface EnableableButtonOverride extends z.infer<
  typeof enableableButtonOverrideSchema_
> {}

const enableableInputOverrideSchema_ = inputStyleOverrideSchema_.extend({
  enabled: z.boolean().meta({ privacy: privacy.nonPii }),
});
export const enableableInputOverrideSchema: z.ZodType<EnableableInputOverride> =
  enableableInputOverrideSchema_;
export interface EnableableInputOverride extends z.infer<
  typeof enableableInputOverrideSchema_
> {}
